// src/EssentialsAdmin.js
// MODIFIED for better mobile responsiveness and linked item names

import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from './App';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';

import ItemFormModal from './ItemFormModal';
import firebaseConfig from './firebaseConfig'; 

function EssentialsAdmin() {
    const { db } = useContext(AppContext);
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showItemModal, setShowItemModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editingCategory, setEditingCategory] = useState(null);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryPriority, setNewCategoryPriority] = useState(10);

    useEffect(() => {
        if (!editingCategory && categories.length > 0) {
            const highestPriority = categories[categories.length - 1].priority || 0;
            setNewCategoryPriority(highestPriority + 10);
        } else if (!editingCategory && categories.length === 0) {
            setNewCategoryPriority(10);
        }
    }, [categories, editingCategory]);

    const fetchData = async () => {
        try {
            const collectionPath = `artifacts/${firebaseConfig.appId}/public/data/`;

            const categoriesQuery = query(collection(db, `${collectionPath}essentials_categories`), orderBy("priority"));
            const catSnap = await getDocs(categoriesQuery);
            setCategories(catSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            const itemSnap = await getDocs(collection(db, `${collectionPath}essentials_items`));
            setItems(itemSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (error) {
            console.error("Error fetching essentials admin data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (db) {
            setLoading(true);
            fetchData();
        }
    }, [db]);

    const handleSaveItem = async (itemData) => {
        const collectionPath = `artifacts/${firebaseConfig.appId}/public/data/essentials_items`;
        if (editingItem) {
            const itemRef = doc(db, collectionPath, editingItem.id);
            await updateDoc(itemRef, itemData);
        } else {
            await addDoc(collection(db, collectionPath), itemData);
        }
        fetchData();
        setShowItemModal(false);
        setEditingItem(null);
    };

    const handleDeleteItem = async (itemId) => {
        const collectionPath = `artifacts/${firebaseConfig.appId}/public/data/essentials_items`;
        if (window.confirm("Are you sure you want to delete this item?")) {
            await deleteDoc(doc(db, collectionPath, itemId));
            fetchData();
        }
    };
    
    const handleCategorySubmit = async (e) => {
        e.preventDefault();
        const collectionPath = `artifacts/${firebaseConfig.appId}/public/data/essentials_categories`;
        if (!newCategoryName.trim()) return;
        
        const priority = Number(newCategoryPriority);

        if (editingCategory) {
            const catRef = doc(db, collectionPath, editingCategory.id);
            await updateDoc(catRef, { name: newCategoryName, priority: priority });
        } else {
            const highestPriority = categories.length > 0 ? categories[categories.length - 1].priority || 0 : 0;
            await addDoc(collection(db, collectionPath), { name: newCategoryName, priority: highestPriority + 10 });
        }
        setNewCategoryName('');
        setEditingCategory(null);
        fetchData();
    };

    const handleDeleteCategory = async (categoryId) => {
        const collectionPath = `artifacts/${firebaseConfig.appId}/public/data/essentials_categories`;
        if (window.confirm("Are you sure? Items in this category will NOT be deleted.")) {
            await deleteDoc(doc(db, collectionPath, categoryId));
            fetchData();
        }
    };

    const handleStartEditCategory = (cat) => {
        setEditingCategory(cat);
        setNewCategoryName(cat.name);
        setNewCategoryPriority(cat.priority);
    };

    const handleCancelEdit = () => {
        setEditingCategory(null);
        setNewCategoryName('');
    };

    if (loading) return <p className="p-4">Loading management console...</p>;

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 space-y-8">
            <h2 className="text-2xl font-bold">Manage UK Essentials Page</h2>

            {/* Category Management */}
            <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-lg shadow">
                <h3 className="font-bold text-lg mb-4">{editingCategory ? 'Edit Category' : 'Add New Category'}</h3>
                {/* ✨ MODIFIED: Added responsive classes for better stacking on mobile */}
                <form onSubmit={handleCategorySubmit} className="flex flex-col sm:flex-row sm:items-start gap-2 mb-4">
                    <input
                        type="number"
                        value={newCategoryPriority}
                        onChange={e => setNewCategoryPriority(e.target.value)}
                        placeholder="Priority"
                        className="p-2 border rounded-md w-full sm:w-24 dark:bg-gray-700 dark:border-gray-600"
                        readOnly={!editingCategory} 
                    />
                    <input
                        type="text"
                        value={newCategoryName}
                        onChange={e => setNewCategoryName(e.target.value)}
                        placeholder="Enter category name"
                        className="flex-grow p-2 border rounded-md w-full dark:bg-gray-700 dark:border-gray-600"
                        required
                    />
                    <div className="flex gap-2 w-full sm:w-auto">
                        <button type="submit" className="flex-grow px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                            {editingCategory ? 'Update' : 'Add'}
                        </button>
                        {editingCategory && (
                            <button type="button" onClick={handleCancelEdit} className="flex-grow px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600">
                                Cancel
                            </button>
                        )}
                    </div>
                </form>
                <ul className="space-y-2">
                    {categories.map(cat => (
                        // ✨ MODIFIED: Added responsive classes for better stacking on mobile
                        <li key={cat.id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-2 bg-gray-100 dark:bg-gray-700 rounded">
                            <span className="mb-2 sm:mb-0">{cat.name} (Priority: {cat.priority})</span>
                            <div className="flex-shrink-0">
                                <button onClick={() => handleStartEditCategory(cat)} className="text-blue-500 hover:underline mr-4">Edit</button>
                                <button onClick={() => handleDeleteCategory(cat.id)} className="text-red-500 hover:underline">Delete</button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Items Management */}
            <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-lg shadow">
                 {/* ✨ MODIFIED: Added responsive classes for better stacking on mobile */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                    <h3 className="font-bold text-lg">Manage Items</h3>
                    <button onClick={() => { setEditingItem(null); setShowItemModal(true); }} className="px-4 py-2 w-full sm:w-auto bg-green-600 text-white rounded-md hover:bg-green-700">
                        Add New Item
                    </button>
                </div>
                {categories.map(cat => (
                    <div key={cat.id} className="mb-6">
                        <h4 className="font-semibold border-b dark:border-gray-600 pb-2 mb-2">{cat.name}</h4>
                        <ul className="space-y-2">
                            {items.filter(i => i.categoryId === cat.id).map(item => (
                                // ✨ MODIFIED: Added responsive classes for better stacking on mobile
                                <li key={item.id} className="flex flex-col sm:flex-row justify-between sm:items-center p-2 gap-2">
                                    <div className="flex items-center w-full min-w-0">
                                        {item.imageUrl && (
                                            <img 
                                                src={item.imageUrl} 
                                                alt={item.name} 
                                                className="w-12 h-12 object-cover rounded mr-4 flex-shrink-0"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        )}
                                        {/* ✨ MODIFIED: Changed span to an anchor tag for the affiliate link */}
                                        <a 
                                            href={item.amazonLink} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="truncate pr-4 hover:underline"
                                        >
                                            {item.name}
                                        </a>
                                    </div>
                                    <div className="flex-shrink-0 self-end sm:self-center">
                                        <button onClick={() => { setEditingItem(item); setShowItemModal(true); }} className="text-blue-500 hover:underline mr-4">Edit</button>
                                        <button onClick={() => handleDeleteItem(item.id)} className="text-red-500 hover:underline">Delete</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            {showItemModal && (
                <ItemFormModal
                    item={editingItem}
                    categories={categories}
                    onSave={handleSaveItem}
                    onClose={() => { setShowItemModal(false); setEditingItem(null); }}
                />
            )}
        </div>
    );
}

export default EssentialsAdmin;